import type { ToolUseItem } from '../../../../contracts/harness.types';

import type { LatestTodoPlan } from './computeLatestTodoPlan';
import { planStateAt, showsPlanCard } from './planCardVisibility';
import { parseProposedPlanInput } from './todoState';
import { CREATE_TODO_PLAN_TOOL_NAME, type TodoState } from './types';

export type PlanCreatePresentation =
  | { kind: 'pending-approval' }
  | { kind: 'in-flight'; proposed: TodoState }
  | { kind: 'declined'; proposed: TodoState }
  | { kind: 'anchor' }
  | { kind: 'suppress' };

/**
 * Deny / timeout, or the error-without-result race before the snapshot
 * hydrates a verdict. `cancelled` is not a race: `effectiveStatus` only
 * yields it when a tool result exists.
 */
const isDeclinedCreateVerdict = (item: ToolUseItem): boolean => {
  const approvalStatus = item.approval?.status;
  if (approvalStatus === 'denied' || approvalStatus === 'timed_out') return true;
  return item.status === 'error' && !item.result && approvalStatus !== 'approved';
};

/**
 * Single verdict for a `create_todo_plan` row. `isSuppressedTodoToolUse` and
 * `renderTodoToolUse` must not switch on `item.status` independently — that
 * drift leaked queued-without-approved creates to the generic shimmer row.
 *
 * Call only for `create_todo_plan`. Todo ops keep their own suppress logic.
 */
export const planCreatePresentation = (
  item: ToolUseItem,
  plan: LatestTodoPlan | null | undefined
): PlanCreatePresentation => {
  if (item.name !== CREATE_TODO_PLAN_TOOL_NAME) return { kind: 'suppress' };

  if (item.status === 'pending_approval') return { kind: 'pending-approval' };

  const proposed = parseProposedPlanInput(item.input);
  if (proposed && isDeclinedCreateVerdict(item)) {
    return { kind: 'declined', proposed };
  }

  if (item.status === 'running') {
    return proposed && showsPlanCard(item, proposed)
      ? { kind: 'in-flight', proposed }
      : { kind: 'suppress' };
  }

  if (item.status === 'queued') {
    if (item.approval?.status === 'approved') {
      return proposed ? { kind: 'in-flight', proposed } : { kind: 'suppress' };
    }
    return { kind: 'suppress' };
  }

  if (item.status === 'complete' && showsPlanCard(item, planStateAt(plan, item.id))) {
    return { kind: 'anchor' };
  }

  return { kind: 'suppress' };
};
